/**
 * Minimum age (seconds) of the cached dashboard before a mount or tab-refocus
 * triggers a refetch. The dashboard endpoint fans out into several DB queries,
 * and mount + visibilitychange used to double-fire it within the same second
 * (visible as duplicate request bursts in the prod logs).
 */
export const DASHBOARD_REFETCH_MIN_AGE_S = 15;

interface ShouldRefetchArgs {
	/** A dashboard request is already in flight. */
	isFetching: boolean;
	/** RTK Query's timestamp of the last fulfilled fetch (undefined before the first). */
	fulfilledTimeStamp: number | undefined;
	now: number;
	minAgeMs?: number;
}

/**
 * Gate for the visibilitychange refetch: skip when a request is already running
 * or the data is fresher than the minimum age — otherwise returning to the tab
 * right after mount duplicates the request.
 */
export const shouldRefetchOnVisibility = ({
	isFetching,
	fulfilledTimeStamp,
	now,
	minAgeMs = DASHBOARD_REFETCH_MIN_AGE_S * 1000,
}: ShouldRefetchArgs): boolean => {
	if (isFetching) {
		return false;
	}

	// Never fetched successfully yet — let the refetch through.
	if (!fulfilledTimeStamp) {
		return true;
	}

	return now - fulfilledTimeStamp >= minAgeMs;
};
